/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import { useContext, useState, useEffect, useRef, useMemo } from 'react';
import { IconChevronDown } from '@tabler/icons-react';
import { Checkbox, Popover } from '@mantine/core';
import { InboundContext } from '../../../../context/InboundContext';
import { Heading, Text } from '../../../styledComponents';
import { getProductListInfo } from '../../../../services/inbound.service';
import { RadioButton } from '../../../common';
import styles from '../FormContainer.module.css';
import { getCurrencyLogo } from '../../../../utils/global';
import CustomTooltip from '../../../common/CustomTooltip/CustomTooltip';
import SelectedProductPreview from './SelectedProductPreview';
import OutOfStockTag from './OutOfStockTag';

type ProductSelectType = {
  selectedProducts: any;
  setSelectedProducts: any;
  formId: string;
};

export default function ProductSelect({ selectedProducts, setSelectedProducts, formId }: ProductSelectType) {
  const { inboundData, defaultCurrency, t } = useContext(InboundContext);
  const [productsList, setProductList] = useState<any>([]);
  const [showProductSelect, setShowProductSelect] = useState<boolean>(false);
  const [isOutOfStockEnabled, setIsOutOfStockEnabled] = useState<boolean>(false);

  const productSelectRef = useRef<null | HTMLDivElement>(null);

  const handleSelectProduct = (product: any) => {
    setSelectedProducts([product]);
    setShowProductSelect(false);
  };

  const isSelectionLimitReached = useMemo(
    () => inboundData?.formSetupPage.maxGiftSelection === selectedProducts.length,
    [inboundData, selectedProducts]
  );

  const handleMultiSelectProduct = (product: any, checked: boolean) => {
    if (isSelectionLimitReached && checked) return;
    if (checked) {
      setSelectedProducts([...selectedProducts, product]);
    } else {
      setSelectedProducts(selectedProducts.filter((o: any) => o.id !== product.id));
    }
  };

  const fetchProductListInfo = async () => {
    if (!inboundData?.formSetupPage?.giftOption?.isActive) return;
    const body = { productIds: inboundData?.formSetupPage?.giftOption?.productIds, formId };
    const response = await getProductListInfo(body);
    if (response.status) {
      const productList: any[] = [];
      const products = response.data?.products || [];
      products.forEach((eachProduct: any) => {
        if (eachProduct?.variants?.[0]?.option1 === 'Default Title') {
          // The first condition check for when there is no variant in shopify.
          const variant = eachProduct?.variants?.[0];
          productList.push({
            id: Math.random(),
            productId: eachProduct.id,
            variantId: variant?.variantId,
            title: eachProduct?.name,
            price: variant?.price || eachProduct?.price,
            quantity: 1,
            productImage: eachProduct?.image,
            variantDetails: {},
            availableQuantity: variant?.quantity,
          });
          return;
        }
        if (eachProduct?.variants?.length === 0) {
          // This condition checks for when variant list is empty for woo commerce
          productList.push({
            id: Math.random(),
            productId: eachProduct.id,
            variantId: eachProduct.id,
            title: eachProduct?.name,
            price: eachProduct?.price,
            quantity: 1,
            productImage: eachProduct?.image,
            variantDetails: {},
          });
          return;
        }

        const optionNames: Record<string, string> = {};
        // Looping over options
        eachProduct?.options?.forEach((option: any, index: number) => {
          optionNames[`option${index + 1}`] = option?.name;
        });

        eachProduct?.variants?.forEach((variant: any) => {
          const variantDetails: Record<string, any> = {};
          if (variant?.option1) {
            variantDetails[optionNames.option1] = variant?.option1;
          }
          if (variant?.option2) {
            variantDetails[optionNames.option2] = variant?.option2;
          }
          if (variant?.option3) {
            variantDetails[optionNames.option3] = variant?.option3;
          }
          productList.push({
            id: Math.random(),
            productId: eachProduct.id,
            variantId: variant?.variantId,
            title: eachProduct?.name,
            price: variant?.price,
            quantity: 1,
            productImage: eachProduct?.image,
            variantDetails,
            availableQuantity: variant?.quantity,
          });
        });
      });
      setProductList(productList);
      setIsOutOfStockEnabled(response.data?.showOutOfStockTags);
      if (productList?.length === 1) {
        handleSelectProduct(productList[0]);
      }
    }
  };

  useEffect(() => {
    fetchProductListInfo();
  }, []);

  if (!inboundData?.formSetupPage?.giftOption?.isActive) {
    return null;
  }

  return (
    <div>
      <div className={styles.formSection}>
        <Heading as="h3" fontWeight="500" fontSize="16px" color="#353535" textTransform="uppercase">
          {t('chooseProductHeader')}
        </Heading>
        <Text color="#667085" fontSize="14px" style={{ marginTop: '-12px' }}>
          {t('chooseProductSubTitle')}
        </Text>
        <div className={styles.selectProductDropdown}>
          <Text color="#667085" fontSize="14px">
            {t('selectProductLabel')}
          </Text>
          <Popover
            opened={showProductSelect}
            width={productSelectRef?.current?.offsetWidth}
            onClose={() => setShowProductSelect(false)}
          >
            <Popover.Target>
              <div
                className={styles.searchInput}
                onClick={() => {
                  setShowProductSelect((v) => !v);
                }}
                ref={productSelectRef}
              >
                {selectedProducts.length ? (
                  <Text fontSize="14px" color="var(--black)">
                    {selectedProducts.map((p: any) => p.title).join(', ')}
                  </Text>
                ) : (
                  <span>{t('selectProductPlaceholder')}</span>
                )}
                <IconChevronDown
                  size={20}
                  color="#667085"
                  strokeWidth={1.5}
                  style={{ rotate: showProductSelect ? '180deg' : '' }}
                />
              </div>
            </Popover.Target>

            <Popover.Dropdown onClick={(e) => e.stopPropagation()} style={{ padding: 0 }}>
              <div className={styles.productOptions}>
                {productsList?.map((product: any) => {
                  const isChecked = selectedProducts.find((p: any) => p?.id === product?.id) || false;
                  return (
                    <div key={product.id} className={styles.productContainer}>
                      <div className={styles.productSelectContainer}>
                        {inboundData.formSetupPage.maxGiftSelection > 1 ? (
                          <CustomTooltip
                            title={`You are only allowed to select ${inboundData?.formSetupPage.maxGiftSelection} products`}
                            show={isSelectionLimitReached && !isChecked}
                          >
                            <Checkbox
                              onChange={(e: any) => {
                                handleMultiSelectProduct(product, e.target.checked);
                              }}
                              checked={isChecked}
                            />
                          </CustomTooltip>
                        ) : (
                          <RadioButton
                            onChange={() => {
                              handleSelectProduct(product);
                            }}
                            checked={isChecked}
                          />
                        )}
                      </div>
                      <div>
                        <div className={styles.productHeader}>
                          <img src={product?.productImage} alt="" width={58} />
                          <div className={styles.details}>
                            <div className={styles.productTitleContainer}>
                              <Heading fontSize="16px" fontWeight="500">
                                {product.title}
                              </Heading>
                              {product.availableQuantity <= 0 && isOutOfStockEnabled && <OutOfStockTag />}
                            </div>

                            <Text fontSize="14px">
                              {getCurrencyLogo(defaultCurrency)}
                              {product?.price}
                            </Text>
                          </div>
                        </div>
                        <div className={styles.variantDetailContainer}>
                          {Object.entries(product?.variantDetails as Record<string, string>)?.map(([title, value]) => (
                            <div key={`${title}-${value}`}>
                              <span className={styles.variantOptionTitle}>{title}: </span>
                              <span className={styles.variantOptionValue}>{value}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Popover.Dropdown>
          </Popover>
        </div>
        <div>
          {selectedProducts.map((product: any) => (
            <SelectedProductPreview
              key={product.id}
              imageUrl={product?.productImage}
              name={product.title}
              variantDetails={product?.variantDetails}
              price={product?.price}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
